import React, { useState, useEffect, useRef } from 'react';
import { Icon } from './icons';
import { Search, Send, MoreHorizontal, Edit3, X, Calendar, Phone, Video, Users } from './icons';
import { db } from '../firebase';
import { 
  collection, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  addDoc, 
  getDocs,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import ExtendedAgenda from './ExtendedAgenda';
import { getAvatarUrl } from '../utils/avatarUtils';
import JitsiCall from './JitsiCall';
import GroupCallInviteModal from './GroupCallInviteModal';
import IncomingCallNotification from './IncomingCallNotification';
import { groupCallService, CallParticipant, GroupCallInvite } from '../services/groupCallService';

interface ChatRoom {
  id: string;
  participants: string[];
  participantNames: string[];
  participantPhotos: string[];
  lastMessage?: string;
  lastMessageAt?: Date;
  messageCount: number;
  unreadCount: { [userId: string]: number };
  createdAt: Date;
}

interface ChatMessage {
  id: string;
  chatRoomId: string;
  senderId: string;
  senderName: string;
  senderPhoto?: string;
  message: string;
  timestamp: Date;
  messageType: 'text' | 'system' | 'call_invite';
  callData?: {
    meetingUrl?: string;
    callType?: 'audio' | 'video';
    chatRoomId?: string;
    isGroupCall?: boolean;
    groupCallId?: string;
    roomId?: string;
  };
}

interface EntityMembership {
  entityId: string;
  entityName: string;
  entityType: 'networkclub' | 'mastermind' | 'community';
}

const ChatInterface = () => {
  const [user] = useAuthState(auth);
  const [activeTab, setActiveTab] = useState<'alle' | 'ongelezen'>('alle');
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAgenda, setShowAgenda] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sharedMemberships, setSharedMemberships] = useState<Record<string, EntityMembership[]>>({});
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ==========================================
  // ✅ EMBEDDED CALL STATE
  // ==========================================
  const [activeCall, setActiveCall] = useState<{
    type: 'audio' | 'video';
    otherUserId: string;
    otherUserName: string;
    // Group call fields
    isGroupCall?: boolean;
    roomId?: string;
    participants?: Array<{ uid: string; name: string }>;
    groupCallId?: string;
  } | null>(null);

  const [showGroupCallModal, setShowGroupCallModal] = useState(false);

  const emojis = ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👹', '👺', '🤡', '💩', '👻', '💀', '☠️', '👽', '👾', '🤖', '🎃', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾'];

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load chat rooms
  useEffect(() => {
    if (!user?.uid) return;

    const roomsQuery = query(
      collection(db, 'chatRooms'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(roomsQuery, (snapshot) => {
      const roomsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        lastMessageAt: doc.data().lastMessageAt?.toDate()
      })) as ChatRoom[];

      // Sort by lastMessageAt desc
      roomsData.sort((a, b) => {
        const at = a.lastMessageAt ? a.lastMessageAt.getTime() : 0;
        const bt = b.lastMessageAt ? b.lastMessageAt.getTime() : 0;
        return bt - at;
      });

      setChatRooms(roomsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Check for selectedChatRoomId from localStorage (after accepting connection)
  useEffect(() => {
    const selectedRoomId = localStorage.getItem('selectedChatRoomId');
    if (selectedRoomId && chatRooms.length > 0) {
      const room = chatRooms.find(r => r.id === selectedRoomId);
      if (room) {
        console.log('✅ Selecting chat room from localStorage:', selectedRoomId);
        localStorage.removeItem('selectedChatRoomId');
        setSelectedRoom(room);
        setMobileView('detail');
      }
    }
  }, [chatRooms]);

  // Load messages for selected room
  useEffect(() => {
    if (!selectedRoom?.id) {
      setMessages([]);
      return;
    }

    const messagesQuery = query(
      collection(db, 'chatMessages'),
      where('chatRoomId', '==', selectedRoom.id),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      })) as ChatMessage[];

      setMessages(messagesData);
    });

    return () => unsubscribe();
  }, [selectedRoom]);

  // Load shared entity memberships for all contacts
  useEffect(() => {
    if (!user?.uid || chatRooms.length === 0) return;

    const loadSharedMemberships = async () => {
      try {
        // Get current user's entity memberships
        const userMembershipsQuery = query(
          collection(db, 'entityMembers'),
          where('userId', '==', user.uid),
          where('status', '==', 'active')
        );
        const userMembershipsSnap = await getDocs(userMembershipsQuery);
        const userEntityIds = userMembershipsSnap.docs.map(doc => doc.data().entityId);

        if (userEntityIds.length === 0) return;

        // For each contact, find shared memberships
        const sharedMap: Record<string, EntityMembership[]> = {};

        for (const room of chatRooms) {
          const otherUserId = getOtherParticipantId(room);
          if (!otherUserId) continue;

          // Get other user's memberships
          const otherMembershipsQuery = query(
            collection(db, 'entityMembers'),
            where('userId', '==', otherUserId),
            where('status', '==', 'active')
          );
          const otherMembershipsSnap = await getDocs(otherMembershipsQuery);
          
          // Find shared entities
          const shared: EntityMembership[] = [];
          for (const memberDoc of otherMembershipsSnap.docs) {
            const entityId = memberDoc.data().entityId;
            if (userEntityIds.includes(entityId)) {
              // Get entity details
              const entityDoc = await getDoc(doc(db, 'entities', entityId));
              if (entityDoc.exists()) {
                const entityData = entityDoc.data();
                shared.push({
                  entityId,
                  entityName: entityData.name || 'Onbekend',
                  entityType: entityData.type || 'community'
                });
              }
            }
          }

          if (shared.length > 0) {
            sharedMap[otherUserId] = shared;
          }
        }

        setSharedMemberships(sharedMap);
      } catch (error) {
        console.error('Error loading shared memberships:', error);
      }
    };

    loadSharedMemberships();
  }, [user, chatRooms]);

  const getOtherParticipantName = (room: ChatRoom) => {
    if (!user) return 'Onbekend';
    const otherIndex = room.participants.findIndex(id => id !== user.uid);
    return otherIndex !== -1 ? room.participantNames[otherIndex] : 'Onbekend';
  };

  const getOtherParticipantPhoto = (room: ChatRoom) => {
    if (!user) return '';
    const otherIndex = room.participants.findIndex(id => id !== user.uid);
    return otherIndex !== -1 ? (room.participantPhotos[otherIndex] || '') : '';
  };

  const getOtherParticipantId = (room: ChatRoom) => {
    if (!user) return '';
    return room.participants.find(id => id !== user.uid) || '';
  };

  const handleSelectRoom = (room: ChatRoom) => {
    setSelectedRoom(room);
    setMobileView('detail');
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedRoom || !user) return;

    try {
      const messageData = {
        chatRoomId: selectedRoom.id,
        senderId: user.uid,
        senderName: user.displayName || 'Onbekend',
        senderPhoto: user.photoURL,
        message: messageInput,
        timestamp: serverTimestamp(),
        messageType: 'text'
      };

      await addDoc(collection(db, 'chatMessages'), messageData);

      // Update room with last message
      await updateDoc(doc(db, 'chatRooms', selectedRoom.id), {
        lastMessage: messageInput,
        lastMessageAt: serverTimestamp(),
        messageCount: selectedRoom.messageCount + 1
      });

      setMessageInput('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // ==========================================
  // ✅ EMBEDDED CALL - Start 1-on-1 call (in-app, geen externe pagina)
  // ==========================================
  const startCall = async (callType: 'audio' | 'video') => {
    if (!selectedRoom || !user) return;

    try {
      const callMessage = callType === 'video' ? '📹 Videogesprek gestart' : '📞 Belletje gestart';
      
      const messageData = {
        chatRoomId: selectedRoom.id,
        senderId: user.uid,
        senderName: user.displayName || 'Onbekend',
        senderPhoto: user.photoURL,
        message: callMessage,
        timestamp: serverTimestamp(),
        messageType: 'call_invite' as const,
        callData: {
          callType,
          chatRoomId: selectedRoom.id
        }
      };

      await addDoc(collection(db, 'chatMessages'), messageData);

      // Update room with last message
      await updateDoc(doc(db, 'chatRooms', selectedRoom.id), {
        lastMessage: callMessage,
        lastMessageAt: serverTimestamp()
      });
      
      // ✅ Open embedded call IN the app (no external URL!)
      const otherIndex = selectedRoom.participants.findIndex(p => p !== user.uid);
      setActiveCall({
        type: callType,
        otherUserId: selectedRoom.participants[otherIndex] || '',
        otherUserName: selectedRoom.participantNames?.[otherIndex] || 'Deelnemer'
      });
      
    } catch (error) {
      console.error('Error starting call:', error);
    }
  };

  // ==========================================
  // ✅ GROUP CALL - Start group call with invitations
  // ==========================================
  const startGroupCall = async (participants: CallParticipant[], callType: 'audio' | 'video') => {
    if (!user) return;

    try {
      const host: CallParticipant = {
        uid: user.uid,
        displayName: user.displayName || 'Gebruiker',
        photoURL: user.photoURL || undefined
      };

      // Create group call in Firestore (sends invites automatically)
      const groupCall = await groupCallService.createGroupCall(host, participants, callType);

      // Send system message to current chat (if a room is selected)
      if (selectedRoom) {
        const names = participants.map(p => p.displayName).join(', ');
        const callMessage = callType === 'video'
          ? `📹 Groeps-videogesprek gestart met ${names}`
          : `📞 Groeps-audiogesprek gestart met ${names}`;

        await addDoc(collection(db, 'chatMessages'), {
          chatRoomId: selectedRoom.id,
          senderId: user.uid,
          senderName: user.displayName || 'Onbekend',
          senderPhoto: user.photoURL,
          message: callMessage,
          timestamp: serverTimestamp(),
          messageType: 'call_invite' as const,
          callData: {
            callType,
            isGroupCall: true,
            groupCallId: groupCall.id,
            roomId: groupCall.roomId
          }
        });

        await updateDoc(doc(db, 'chatRooms', selectedRoom.id), {
          lastMessage: callMessage,
          lastMessageAt: serverTimestamp()
        });
      }

      // Open embedded group call
      setActiveCall({
        type: callType,
        otherUserId: participants[0]?.uid || '',
        otherUserName: participants.map(p => p.displayName).join(', '),
        isGroupCall: true,
        roomId: groupCall.roomId,
        participants: [
          { uid: user.uid, name: user.displayName || 'Gebruiker' },
          ...participants.map(p => ({ uid: p.uid, name: p.displayName }))
        ],
        groupCallId: groupCall.id
      });

      setShowGroupCallModal(false);
      console.log('✅ Group call started:', groupCall.id);
    } catch (error) {
      console.error('❌ Error starting group call:', error);
    }
  };

  // ==========================================
  // ✅ Handle incoming group call invite acceptance
  // ==========================================
  const handleAcceptIncomingCall = (invite: GroupCallInvite) => {
    setActiveCall({
      type: invite.callType,
      otherUserId: invite.invitedByUserId,
      otherUserName: invite.participantNames.join(', '),
      isGroupCall: true,
      roomId: invite.roomId,
      groupCallId: invite.groupCallId
    });
  };

  // ==========================================
  // ✅ End call handler
  // ==========================================
  const handleEndCall = async () => {
    if (activeCall?.groupCallId) {
      // If host, end the group call for everyone
      try {
        const groupCall = await groupCallService.getGroupCall(activeCall.groupCallId);
        if (groupCall && groupCall.hostUserId === user?.uid) {
          await groupCallService.endGroupCall(activeCall.groupCallId);
        }
      } catch (error) {
        console.error('Error ending group call:', error);
      }
    }
    setActiveCall(null);
  };

  const addEmoji = (emoji: string) => {
    setMessageInput(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'networkclub': return '🏢';
      case 'mastermind': return '🧠';
      case 'community': return '👥';
      default: return '🏢';
    }
  };

  const getEntityLabel = (type: string) => {
    switch (type) {
      case 'networkclub': return 'Netwerkclub';
      case 'mastermind': return 'Mastermind';
      case 'community': return 'Community';
      default: return 'Club';
    }
  };

  // Filter rooms based on search and tab
  const filteredRooms = chatRooms.filter(room => {
    const name = getOtherParticipantName(room).toLowerCase();
    const matchesSearch = name.includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'alle' || 
      (activeTab === 'ongelezen' && (room.unreadCount[user?.uid || ''] || 0) > 0);
    return matchesSearch && matchesTab;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3ECBB5] mx-auto mb-4"></div>
          <p className="text-gray-600">Berichten laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full md:h-[600px] bg-[#F4F1EA] md:bg-white overflow-hidden">
      {/* Left Sidebar */}
      <div className={`${selectedRoom && mobileView === 'detail' ? 'hidden md:flex' : 'flex'} w-full md:w-80 border-r border-gray-200 flex-col bg-[#F4F1EA] md:bg-white overflow-x-hidden`}>
        {/* Mobile padding wrapper — prevents margin-based overflow */}
        <div className="flex flex-col flex-1 min-h-0 px-4 pt-4 pb-4 gap-3 md:p-0 md:gap-0">
        {/* Header */}
        <div className="rounded-2xl border border-[#E8E4DC] bg-white/90 p-4 shadow-sm md:rounded-none md:border-0 md:border-b md:border-gray-200 md:bg-white md:shadow-none md:m-0">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold text-gray-900 whitespace-nowrap">Berichten</h1>
            <button
              onClick={() => setShowAgenda(true)}
              className="p-2 hover:bg-[#F0F7F4] rounded-lg transition-colors group flex-shrink-0"
              title="Afspraak plannen"
            >
              <Icon icon={Calendar} size={20} strokeWidth={1.5} className="text-gray-500 group-hover:text-[#8DA8A0]" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {(['alle', 'ongelezen'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'text-white'
                    : 'text-[#5A5A5A] hover:text-[#1C1C1C]'
                }`}
                style={activeTab === tab ? { background: '#8DA8A0', color: '#FFFFFF' } : {}}
              >
                {tab === 'alle' ? 'Alle' : 'Ongelezen'}
              </button>
            ))}
          </div>
          
          {/* Search */}
          <div className="relative mt-3">
            <Icon icon={Search} size={16} strokeWidth={1.5} color="#9ca3af" className="absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Berichten zoeken"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#E0DDD8] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#3ECBB5] focus:border-transparent bg-white text-[#1C1C1C] placeholder:text-[#9A9A9A]"
            />
          </div>
        </div>

        {/* Contacts List */}
        <div className="flex-1 overflow-y-auto rounded-2xl border border-[#E8E4DC] bg-white/90 shadow-sm md:rounded-none md:border-0 md:bg-white md:shadow-none">
          {filteredRooms.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <div className="text-4xl mb-2">💬</div>
              <p className="text-sm">
                {searchQuery ? 'Geen berichten gevonden' : 'Nog geen gesprekken'}
              </p>
              {!searchQuery && (
                <p className="text-xs mt-1">Start een chat vanuit Connecties</p>
              )}
            </div>
          ) : (
            filteredRooms.map((room) => {
              const otherName = getOtherParticipantName(room);
              const otherPhoto = getOtherParticipantPhoto(room);
              const otherId = getOtherParticipantId(room);
              const unread = room.unreadCount[user?.uid || ''] || 0;
              const shared = sharedMemberships[otherId] || [];

              return (
                <div
                  key={room.id}
                  onClick={() => handleSelectRoom(room)}
                  className={`flex items-start p-3 cursor-pointer border-l-4 transition-colors ${
                    selectedRoom?.id === room.id
                      ? 'border-l-[#3ECBB5]'
                      : 'border-l-transparent hover:bg-[#F7F5F0]'
                  }`}
                  style={selectedRoom?.id === room.id ? { background: 'rgba(62,203,181,0.07)' } : {}}
                >
                  <div className="relative flex-shrink-0">
                    <img
                      src={getAvatarUrl(otherPhoto, otherId, otherName, '4F6660')}
                      alt={otherName}
                      className="w-12 h-12 rounded-full object-cover"
                      onError={(e) => {
                        const name = otherName || otherId || 'User';
                        const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                        (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=4F6660&color=fff&size=128`;
                      }}
                    />
                  </div>
                  
                  <div className="ml-3 flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {otherName}
                      </p>
                      {room.lastMessageAt && (
                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                          {room.lastMessageAt.toLocaleDateString('nl-NL', { 
                            day: 'numeric', 
                            month: 'short' 
                          })}
                        </span>
                      )}
                    </div>

                    {/* Shared memberships */}
                    {shared.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {shared.slice(0, 2).map((membership) => (
                          <span
                            key={membership.entityId}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border"
                            style={{ background: 'rgba(141,168,160,0.12)', color: '#8DA8A0', borderColor: '#C8D8D0' }}
                            title={`${getEntityLabel(membership.entityType)}: ${membership.entityName}`}
                          >
                            <span>{getEntityIcon(membership.entityType)}</span>
                            <span className="truncate max-w-[100px]">{membership.entityName}</span>
                          </span>
                        ))}
                        {shared.length > 2 && (
                          <span className="text-[10px] text-gray-500">+{shared.length - 2}</span>
                        )}
                      </div>
                    )}
                    
                    <p className="text-sm text-gray-600 truncate mt-1">
                      {room.lastMessage || 'Geen berichten'}
                    </p>
                  </div>
                  
                  {unread > 0 && (
                    <div className="w-5 h-5 bg-[#8DA8A0] rounded-full flex items-center justify-center text-white text-xs font-medium ml-2 flex-shrink-0">
                      {unread}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        </div>{/* end mobile padding wrapper */}
      </div>

      {/* Right Chat Area */}
      <div className={`${selectedRoom && mobileView === 'detail' ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0`}>
        {selectedRoom ? (
          <>
            {/* Chat Header */}
            <div className="px-3 py-3 md:p-4 border-b border-gray-200 flex items-center justify-between bg-white">
              <div className="flex items-center flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => setMobileView('list')}
                  className="mr-2 flex h-9 w-9 items-center justify-center rounded-full text-[#8DA8A0] active:bg-[#F0F7F4] md:hidden"
                  aria-label="Terug naar gesprekken"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <span className="text-lg leading-none">‹</span>
                </button>
                <img
                  src={getAvatarUrl(
                    getOtherParticipantPhoto(selectedRoom),
                    getOtherParticipantId(selectedRoom),
                    getOtherParticipantName(selectedRoom),
                    '4F6660'
                  )}
                  alt={getOtherParticipantName(selectedRoom)}
                  className="w-10 h-10 md:w-10 md:h-10 rounded-full object-cover flex-shrink-0"
                />
                <div className="ml-3 min-w-0 flex-1">
                  <h2 className="font-semibold text-gray-900 truncate">
                    {getOtherParticipantName(selectedRoom)}
                  </h2>
                  {/* Shared memberships in header */}
                  {sharedMemberships[getOtherParticipantId(selectedRoom)]?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {sharedMemberships[getOtherParticipantId(selectedRoom)].slice(0, 2).map((membership) => (
                        <span
                          key={membership.entityId}
                          className="inline-flex items-center gap-1 text-xs text-gray-600"
                          title={`${getEntityLabel(membership.entityType)}: ${membership.entityName}`}
                        >
                          <span>{getEntityIcon(membership.entityType)}</span>
                          <span className="truncate max-w-[150px]">{membership.entityName}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Call buttons */}
              <div className="flex items-center gap-1 ml-2 md:ml-4 flex-shrink-0">
                <button
                  onClick={() => setShowAgenda(true)}
                  className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-gray-100 active:bg-[#F0F7F4]"
                  title="Afspraak of locatie plannen"
                  aria-label="Afspraak of locatie plannen"
                >
                  <Icon icon={Calendar} size={19} strokeWidth={1.5} color="#6b7280" />
                </button>
                <button
                  onClick={() => startCall('audio')}
                  className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-gray-100 active:bg-[#F0F7F4]"
                  title="Bellen"
                  aria-label="Bellen"
                >
                  <Icon icon={Phone} size={19} strokeWidth={1.5} color="#6b7280" />
                </button>
                <button
                  onClick={() => startCall('video')}
                  className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-gray-100 active:bg-[#F0F7F4]"
                  title="Beeldbellen"
                  aria-label="Beeldbellen"
                >
                  <Icon icon={Video} size={19} strokeWidth={1.5} color="#6b7280" />
                </button>
                <button
                  onClick={() => setShowGroupCallModal(true)}
                  className="hidden md:flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[#F0F7F4]"
                  title="Groepsgesprek starten"
                >
                  <Icon icon={Users} size={20} strokeWidth={1.5} color="#4F6660" />
                </button>
                <button className="hidden md:flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-gray-100">
                  <Icon icon={MoreHorizontal} size={20} strokeWidth={1.5} color="#6b7280" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 bg-gray-50" style={{ scrollbarWidth: 'thin' }}>
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3 text-xl">
                    💬
                  </div>
                  <p className="text-sm font-medium text-gray-600 mb-0.5">Start het gesprek</p>
                  <p className="text-xs text-gray-400">Schrijf je eerste bericht aan {getOtherParticipantName(selectedRoom)}</p>
                </div>
              ) : (
                <>
                  {(() => {
                    // Group consecutive messages by sender — Apple/iMessage style
                    // Avatar only on the LAST message of each group (bottom-aligned).
                    // Tighter spacing within a group, more space between groups.
                    const rendered: React.ReactNode[] = [];
                    const visibleMessages = messages.filter(m => {
                      const isOldWelcome = m.message && (
                        m.message.includes('connectieverzoek is geaccepteerd') ||
                        (m.message.includes('Laten we chatten') && m.message.includes('Hallo'))
                      );
                      return m.messageType === 'system' || m.senderId === 'system' || isOldWelcome
                        ? true  // keep system messages
                        : true; // keep all
                    });

                    visibleMessages.forEach((message, idx) => {
                      const isCurrentUser = message.senderId === user?.uid;
                      const isCallInvite = message.messageType === 'call_invite';
                      const isOldWelcome = message.message && (
                        message.message.includes('connectieverzoek is geaccepteerd') ||
                        (message.message.includes('Laten we chatten') && message.message.includes('Hallo'))
                      );
                      const isSystem = message.messageType === 'system' || message.senderId === 'system' || isOldWelcome;

                      // ── System pill ───────────────────────────────────────
                      if (isSystem) {
                        const txt = isOldWelcome && message.messageType !== 'system'
                          ? '🎉 Jullie zijn nu verbonden! Start het gesprek.'
                          : message.message;
                        rendered.push(
                          <div key={message.id} className="flex justify-center my-4">
                            <div className="bg-white border border-gray-200 text-gray-500 px-3 py-1 rounded-full text-xs shadow-sm">
                              {txt}
                            </div>
                          </div>
                        );
                        return;
                      }

                      // ── Group logic ───────────────────────────────────────
                      // Is this the LAST message in a consecutive run by same sender?
                      const nextMsg = visibleMessages[idx + 1];
                      const isLastInGroup = !nextMsg
                        || nextMsg.senderId !== message.senderId
                        || nextMsg.senderId === 'system';

                      const prevMsg = visibleMessages[idx - 1];
                      const isFirstInGroup = !prevMsg
                        || prevMsg.senderId !== message.senderId
                        || prevMsg.senderId === 'system';

                      // Vertical gap: more space at start of a new group
                      const topMargin = isFirstInGroup ? 'mt-3' : 'mt-0.5';

                      // Bubble radius: flatten corners that "stack" inside a group
                      let bubbleRadius = '';
                      if (isCurrentUser) {
                        // Right side: orange
                        if (isFirstInGroup && isLastInGroup) bubbleRadius = 'rounded-2xl rounded-br-sm';
                        else if (isFirstInGroup)             bubbleRadius = 'rounded-2xl rounded-br-sm rounded-bl-2xl';
                        else if (isLastInGroup)              bubbleRadius = 'rounded-2xl rounded-tr-sm rounded-br-sm';
                        else                                 bubbleRadius = 'rounded-2xl rounded-r-sm';
                      } else {
                        // Left side: blue
                        if (isFirstInGroup && isLastInGroup) bubbleRadius = 'rounded-2xl rounded-bl-sm';
                        else if (isFirstInGroup)             bubbleRadius = 'rounded-2xl rounded-bl-sm rounded-br-2xl';
                        else if (isLastInGroup)              bubbleRadius = 'rounded-2xl rounded-tl-sm rounded-bl-sm';
                        else                                 bubbleRadius = 'rounded-2xl rounded-l-sm';
                      }

                      // Avatar slot: always the same width (7 = 28px), either visible or invisible spacer
                      const showAvatar = !isCurrentUser && isLastInGroup;
                      const avatarEl = (
                        <div className="w-7 flex-shrink-0 flex items-end">
                          {showAvatar ? (
                            <img
                              src={message.senderPhoto
                                ? message.senderPhoto
                                : `https://ui-avatars.com/api/?name=${encodeURIComponent(message.senderName)}&background=4F6660&color=fff&size=28`
                              }
                              alt={message.senderName}
                              className="w-7 h-7 rounded-full object-cover"
                              onError={(e) => {
                                const initials = message.senderName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                                (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=4F6660&color=fff&size=28`;
                              }}
                            />
                          ) : (
                            // Invisible spacer — keeps all bubbles aligned
                            <span className="w-7 h-7 block" />
                          )}
                        </div>
                      );

                      rendered.push(
                        <div
                          key={message.id}
                          className={`flex items-end gap-2 ${isCurrentUser ? 'justify-end' : 'justify-start'} ${topMargin}`}
                        >
                          {/* Avatar slot — left side only */}
                          {!isCurrentUser && avatarEl}

                          {/* Bubble */}
                          <div
                            className={`max-w-xs lg:max-w-md px-3.5 py-2 ${
                              isCallInvite
                                ? 'rounded-2xl bg-white border border-gray-200 shadow-sm'
                                : isCurrentUser
                                  ? `${bubbleRadius} bg-[#2E5F56] text-white`
                                  : `${bubbleRadius} bg-[#F0F7F4] text-[#1C1C1C]`
                            }`}
                          >
                            {isCallInvite ? (
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-[rgba(46,95,86,0.08)] flex items-center justify-center flex-shrink-0">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2E5F56" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.72 19.79 19.79 0 01.12 1.14 2 2 0 012.11 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.08 6.08l1.27-.44a2 2 0 012.11.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-xs font-semibold text-gray-700">{message.message}</p>
                                  <button
                                    onClick={() => {
                                      if (message.callData?.isGroupCall && message.callData?.roomId) {
                                        setActiveCall({
                                          type: (message.callData?.callType as 'audio' | 'video') || 'video',
                                          otherUserId: message.senderId,
                                          otherUserName: message.senderName,
                                          isGroupCall: true,
                                          roomId: message.callData.roomId as string,
                                          groupCallId: message.callData.groupCallId as string
                                        });
                                      } else if (selectedRoom) {
                                        const otherIndex = selectedRoom.participants.findIndex(p => p !== user?.uid);
                                        setActiveCall({
                                          type: (message.callData?.callType as 'audio' | 'video') || 'video',
                                          otherUserId: selectedRoom.participants[otherIndex] || '',
                                          otherUserName: selectedRoom.participantNames?.[otherIndex] || 'Deelnemer'
                                        });
                                      }
                                    }}
                                    className="text-xs text-[#2E5F56] font-semibold mt-0.5 hover:underline"
                                  >
                                    Deelnemen →
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                                {message.message}
                              </p>
                            )}

                            {/* Timestamp — only show on last in group */}
                            {isLastInGroup && (
                              <p className={`text-[10px] mt-1 ${
                                isCallInvite ? 'text-gray-400' : 'text-white/60'
                              } ${isCurrentUser ? 'text-right' : 'text-left'}`}>
                                {message.timestamp.toLocaleTimeString('nl-NL', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            )}
                          </div>

                          {/* Invisible spacer — right side, keeps bubble aligned */}
                          {isCurrentUser && <span className="w-7 flex-shrink-0" />}
                        </div>
                      );
                    });

                    return rendered;
                  })()}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Input */}
            <div className="px-4 py-3 border-t border-gray-100 bg-white">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Typ een bericht…"
                    className="w-full px-4 py-2.5 bg-gray-100 border-0 rounded-2xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3ECBB5] focus:bg-white transition-colors"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                </div>
                
                {/* Emoji Picker Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-lg leading-none"
                  >
                    😊
                  </button>
                  
                  {/* Emoji Picker */}
                  {showEmojiPicker && (
                    <div className="absolute bottom-12 right-0 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-64 h-48 overflow-y-auto z-10">
                      <div className="grid grid-cols-8 gap-1">
                        {emojis.map((emoji, index) => (
                          <button
                            key={index}
                            onClick={() => addEmoji(emoji)}
                            className="text-lg hover:bg-gray-100 rounded p-1 transition-colors"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Send button — icon only, always visible */}
                <button
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim()}
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#2E5F56] hover:bg-[#265049] disabled:opacity-35 disabled:cursor-not-allowed transition-all flex-shrink-0"
                  aria-label="Bericht versturen"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 bg-gray-50">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4 mx-auto">
                💬
              </div>
              <p>Selecteer een gesprek om te beginnen</p>
            </div>
          </div>
        )}
      </div>

      {/* ==========================================
          ✅ EMBEDDED CALL OVERLAY
          ========================================== */}
      {activeCall && user && (
        <JitsiCall
          currentUserId={user.uid}
          currentUserName={user.displayName || 'Gebruiker'}
          otherUserId={activeCall.isGroupCall ? undefined : activeCall.otherUserId}
          otherUserName={activeCall.isGroupCall ? undefined : activeCall.otherUserName}
          participants={activeCall.participants}
          roomId={activeCall.roomId}
          callType={activeCall.type}
          onEndCall={handleEndCall}
        />
      )}

      {/* ==========================================
          ✅ GROUP CALL INVITE MODAL
          ========================================== */}
      {showGroupCallModal && user && (
        <GroupCallInviteModal
          currentUserId={user.uid}
          currentUserName={user.displayName || 'Gebruiker'}
          currentUserPhoto={user.photoURL || undefined}
          onStartCall={startGroupCall}
          onClose={() => setShowGroupCallModal(false)}
        />
      )}

      {/* ==========================================
          ✅ INCOMING CALL NOTIFICATIONS
          ========================================== */}
      {user && (
        <IncomingCallNotification
          userId={user.uid}
          onAcceptCall={handleAcceptIncomingCall}
        />
      )}

      {/* Extended Agenda Overlay */}
      {showAgenda && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-[#F0F7F4] to-[rgba(141,168,160,0.10)]">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-[#2E5F56] rounded-full flex items-center justify-center">
                  <Icon icon={Calendar} size={20} strokeWidth={1.5} color="white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Afspraak Plannen</h2>
                  {selectedRoom && (
                    <p className="text-sm text-gray-600">met {getOtherParticipantName(selectedRoom)}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowAgenda(false)}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <Icon icon={X} size={20} strokeWidth={1.5} color="#6b7280" />
              </button>
            </div>
            
            {/* Agenda Content */}
            <div className="flex-1 overflow-auto">
              <ExtendedAgenda
                selectedConnectionData={selectedRoom ? {
                  id: selectedRoom.id,
                  name: getOtherParticipantName(selectedRoom),
                  photoURL: getOtherParticipantPhoto(selectedRoom),
                  user1Id: user?.uid,
                  user2Id: getOtherParticipantId(selectedRoom)
                } : undefined}
                connectionId={selectedRoom?.id}
                currentUserId={user?.uid}
                onClosePlanning={() => setShowAgenda(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
